/**
 * Plateforme multi événement, (c) Mikaël FOURNY.
 * src/i18n/fr.salon.ts
 *
 * Messages de la page d'exploration de l'événement (/decouvrir, « Le salon »
 * de NOLAS 9d2063a et 1443024) : offres et profils recherchés, exposants,
 * programme, recherche, filtres et tris.
 *
 * Flotte 2026-10-04 :
 *   - variantes « SansOffres » quand le module emploi est coupé (cirfa) :
 *     aucune mention d'offre ni de poste à pourvoir ;
 *   - invitation à se connecter (401) dans `salon.connexion.*`, propre à ce
 *     sous-catalogue, pour ne dépendre d'aucun autre chantier.
 *
 * Vocabulaire piloté par l'événement : le même code sert un salon de
 * l'emploi et un forum de la Défense. Jamais « salon », « atelier »,
 * « exposant » ni nom d'événement en dur : jetons {eventXxx} (identité) et
 * {lexXxx} (lexique), substitués au-dessus de react-intl.
 * Règles typographiques : apostrophe typographique (’) partout, une
 * apostrophe droite devant une accolade casserait le jeton (piège ICU) ;
 * aucun tiret cadratin. Fusionné dans fr.ts.
 *
 * Mikael - chef de projet - 2026-10-04
 */
export const messagesSalonFr: Record<string, string> = {
  // Entrée de la barre du bas : « Le salon », « Le forum »… selon le type
  // d'événement (flotte 2026-10-04 : rangée ici, pas dans le noyau).
  'bottomNav.salon': '{eventTypeDefCap}',

  // ── En-tête ────────────────────────────────────────────────────────────
  'salon.titre': '{eventTypeDefCap}',
  'salon.hero.accroche':
    'Toutes les offres, {lexExhibitorAllDefPlural} et le programme, au même endroit. Cherchez, filtrez, préparez votre journée.',
  'salon.hero.ctaOffres': 'Explorer les offres',
  'salon.hero.ctaProgramme': 'Composer mon programme',
  'salon.hero.accrocheSansOffres':
    '{lexExhibitorAllDefPluralCap} et le programme, au même endroit. Cherchez, filtrez, préparez votre journée.',
  'salon.hero.bientotSansOffres':
    'Les listes ouvrent très bientôt au public. Revenez vite : {lexExhibitorPlural} et programme apparaîtront ici.',
  'salon.hero.bientot':
    'Les listes ouvrent très bientôt au public. Revenez vite : offres, {lexExhibitorPlural} et programme apparaîtront ici.',

  // ── Chiffres clés (libellés sous les compteurs animés) ─────────────────
  'salon.chiffres.label': 'En chiffres',
  'salon.chiffres.exposants': '{n, plural, one {{lexExhibitorBare}} other {{lexExhibitorPlural}}}',
  'salon.chiffres.offres': '{n, plural, one {offre} other {offres}}',
  'salon.chiffres.postes': '{n, plural, one {poste à pourvoir} other {postes à pourvoir}}',
  'salon.chiffres.ateliers': '{n, plural, one {{lexWorkshopBare}} other {{lexWorkshopPlural}}}',

  // ── Mur de logos ───────────────────────────────────────────────────────
  'salon.logos.titre': 'Ils seront là',
  'salon.logos.pause': 'Mettre le défilement en pause',
  'salon.logos.reprendre': 'Reprendre le défilement',
  'salon.logos.lien': 'Voir la fiche de {nom}',
  'salon.logos.tous': 'Voir {lexExhibitorAllDefPlural}',

  // ── Recherche unifiée ──────────────────────────────────────────────────
  'salon.recherche.label': 'Rechercher un métier, {lexExhibitorIndef}, {lexWorkshopIndef}',
  'salon.recherche.placeholder': 'Métier, {lexExhibitorBare}, ville, thème…',
  'salon.recherche.effacer': 'Effacer la recherche',
  'salon.recherche.resume':
    'Pour « {q} » : {nbOffres, plural, =0 {aucune offre} one {# offre} other {# offres}}, {nbExposants, plural, =0 {{lexExhibitorNone}} one {# {lexExhibitorBare}} other {# {lexExhibitorPlural}}}, {nbProgramme, plural, =0 {{lexWorkshopNone}} one {# {lexWorkshopBare}} other {# {lexWorkshopPlural}}}.',

  'salon.recherche.resumeSansOffres':
    'Pour « {q} » : {nbExposants, plural, =0 {{lexExhibitorNone}} one {# {lexExhibitorBare}} other {# {lexExhibitorPlural}}}, {nbProgramme, plural, =0 {{lexWorkshopNone}} one {# {lexWorkshopBare}} other {# {lexWorkshopPlural}}}.',

  // ── Onglets ────────────────────────────────────────────────────────────
  'salon.onglets.label': 'Que voulez-vous explorer ?',
  'salon.onglet.postes': 'Offres',
  'salon.onglet.exposants': '{lexExhibitorPluralCap}',
  'salon.onglet.ateliers': '{lexWorkshopPluralCap}',
  'salon.onglet.nombre': '{n, plural, one {# résultat} other {# résultats}}',

  // ── Filtres et tris ────────────────────────────────────────────────────
  'salon.filtres.label': 'Filtres',
  'salon.filtres.contrat': 'Type de contrat',
  'salon.filtres.tousContrats': 'Tous',
  'salon.filtres.secteur': 'Secteur',
  'salon.filtres.tousSecteurs': 'Tous les secteurs',
  'salon.filtres.niveau': 'Profil recherché',
  'salon.filtres.tousNiveaux': 'Tous les profils',
  'salon.filtres.lieu': 'Lieu',
  'salon.filtres.tousLieux': 'Tous les lieux',
  'salon.filtres.exposant': '{lexExhibitorCap}',
  'salon.filtres.tousExposants': '{lexExhibitorAllDefPluralCap}',
  'salon.filtres.option': '{libelle} ({n})',
  'salon.filtres.dispo': 'Seulement avec des places',
  'salon.filtres.tri': 'Trier par',
  'salon.filtres.effacer': 'Tout effacer',
  'salon.filtres.plus': 'Plus de filtres',
  'salon.filtres.actifs': '{n, plural, one {# filtre actif} other {# filtres actifs}}',

  'salon.tri.postes.pertinence': 'Pertinence',
  'salon.tri.postes.postes': 'Nombre de postes',
  'salon.tri.postes.entreprise': '{lexExhibitorCap} (A à Z)',
  'salon.tri.postes.metier': 'Métier (A à Z)',
  'salon.tri.postes.contrat': 'Type de contrat',
  'salon.tri.exposants.pertinence': 'Pertinence',
  'salon.tri.exposants.az': 'Nom (A à Z)',
  'salon.tri.exposants.postes': 'Nombre de postes',
  'salon.tri.exposants.ville': 'Ville',
  'salon.tri.ateliers.horaire': 'Horaire',
  'salon.tri.ateliers.places': 'Places disponibles',
  'salon.tri.ateliers.titre': 'Titre (A à Z)',

  // ── Offres ─────────────────────────────────────────────────────────────
  'salon.postes.resultat':
    '{offres, plural, =0 {Aucune offre} one {# offre} other {# offres}}, {postes, plural, =0 {aucun poste} one {# poste à pourvoir} other {# postes à pourvoir}}',
  'salon.postes.repartition': 'Répartition des offres par type de contrat',
  'salon.postes.vide':
    'Aucune offre publiée pour l’instant. {lexExhibitorDefPluralCap} les ajoutent au fil des jours.',
  'salon.poste.nbPostes': '{n, plural, one {# poste} other {# postes}}',
  'salon.poste.profil': 'Profil recherché',
  'salon.poste.profilMasquer': 'Masquer le profil',
  'salon.poste.niveau': 'Expérience attendue : {niveau}',
  'salon.poste.contrat': 'Contrat : {contrat}',
  'salon.poste.lieu': 'Lieu : {lieu}',
  'salon.poste.chargement': 'Chargement du profil…',
  'salon.poste.sansDescription':
    'Le détail du profil n’est pas encore rédigé. La fiche complète, et l’équipe sur place, vous en diront plus.',
  'salon.poste.erreur': 'Le profil n’a pas pu être chargé. La fiche complète reste accessible.',
  'salon.poste.voir': 'Voir l’offre',
  'salon.poste.voirAria': 'Voir l’offre {titre} chez {entreprise}',
  'salon.poste.memeEntreprise': 'Toutes ses offres',
  'salon.poste.memeEntrepriseAria': 'Afficher toutes les offres de {nom}',

  // ── Exposants ──────────────────────────────────────────────────────────
  'salon.exposants.resultat':
    '{n, plural, =0 {{lexExhibitorNoneCap}} one {# {lexExhibitorBare}} other {# {lexExhibitorPlural}}}',
  'salon.exposants.vide': '{lexExhibitorNoneCap} publié{lexExhibitorAgr} pour l’instant. La liste se remplit au fil des inscriptions.',
  'salon.exposant.offres': '{n, plural, =0 {Pas d’offre publiée} one {# offre} other {# offres}}',
  'salon.exposant.postes': '{n, plural, one {# poste à pourvoir} other {# postes à pourvoir}}',
  'salon.exposant.plus': 'En savoir plus',
  'salon.exposant.moins': 'Réduire',
  'salon.exposant.chargement': 'Chargement de la fiche…',
  'salon.exposant.erreur': 'La fiche n’a pas pu être chargée. La fiche complète reste accessible.',
  'salon.exposant.presentation': 'Présentation',
  'salon.exposant.proposition': 'Ce que nous proposons',
  'salon.exposant.adresse': 'Adresse',
  'salon.exposant.metiers': 'Métiers proposés',
  'salon.exposant.voirOffres': 'Voir {n, plural, one {son offre} other {ses # offres}}',
  'salon.exposant.fiche': 'Fiche complète',
  'salon.exposant.ficheAria': 'Fiche complète de {nom}',
  'salon.exposant.site': 'Site web',
  'salon.exposant.offresEnLigne': 'Offres en ligne',
  'salon.exposant.candidater': 'Candidater',
  'salon.exposant.spontanee': 'Candidature spontanée',
  'salon.exposant.reseaux': 'Réseaux',
  'salon.exposant.neRecrutePas': 'Présent{lexExhibitorAgr} pour informer et orienter, sans offre d’emploi.',
  'salon.exposant.sansDescription': 'Présentation à venir.',
  'salon.lien.nouvelOnglet': '{libelle} (nouvel onglet)',

  // ── Programme ──────────────────────────────────────────────────────────
  'salon.ateliers.resultat':
    '{n, plural, =0 {{lexWorkshopNoneCap}} one {# {lexWorkshopBare}} other {# {lexWorkshopPlural}}}',
  'salon.ateliers.vide': 'Le programme sera publié très bientôt.',
  'salon.ateliers.programme': 'Programme complet et inscriptions',
  'salon.atelier.horaire': '{debut} à {fin}',
  'salon.atelier.duree': '{n} min',
  'salon.atelier.salle': 'Lieu : {salle}',
  'salon.atelier.intervenant': 'Avec {nom}',
  'salon.atelier.places': '{n, plural, one {# place restante} other {# places restantes}}',
  'salon.atelier.complet': 'Complet',
  'salon.atelier.libre': 'Accès libre',
  'salon.atelier.enCours': 'En cours',
  'salon.atelier.termine': 'Terminé',
  'salon.atelier.creneaux': '{n, plural, one {# créneau} other {# créneaux}}',
  'salon.atelier.lirePlus': 'Lire la suite',
  'salon.atelier.lireMoins': 'Réduire',
  'salon.atelier.voir': 'Détails et inscription',
  'salon.atelier.voirAria': 'Détails et inscription : {titre}',

  // ── États partagés ─────────────────────────────────────────────────────
  'salon.chargement': 'Chargement…',
  'salon.plus': 'Afficher {n} de plus',
  'salon.aucunResultat': 'Aucun résultat. Élargissez la recherche ou retirez un filtre.',
  'salon.bientot.titre': 'Ouverture très prochaine',
  'salon.bientot.postes': 'Les offres seront consultables ici dès l’ouverture au public.',
  'salon.bientot.exposants': 'La liste {lexExhibitorOfPlural} sera consultable ici dès l’ouverture au public.',
  'salon.bientot.ateliers': 'Le programme sera consultable ici dès l’ouverture au public.',
  // Connexion obligatoire (réponse 401) : une invitation, jamais une panne.
  'salon.connexion.titre': 'Réservé aux personnes inscrites',
  'salon.connexion.postes': 'Connectez-vous pour voir les offres.',
  'salon.connexion.exposants': 'Connectez-vous pour voir la liste {lexExhibitorOfPlural}.',
  'salon.connexion.ateliers': 'Connectez-vous pour voir le programme.',
  'salon.connexion.bouton': 'Se connecter',
  'salon.connexion.inscription': 'Pas encore de compte ? S’inscrire',
  'salon.indispo.titre': 'Momentanément indisponible',
  'salon.indispo.texte': 'La liste n’a pas pu être chargée. Vérifiez votre connexion puis réessayez.',
  'salon.indispo.reessayer': 'Réessayer',

  // ── Pied de page ───────────────────────────────────────────────────────
  'salon.fin.titre': 'Prêt pour le grand jour ?',
  'salon.fin.texte':
    'Repérez vos offres, notez les stands à ne pas manquer et inscrivez-vous {lexWorkshopAtPlural} qui vous intéressent.',
  'salon.fin.texteSansOffres':
    'Notez les stands à ne pas manquer et inscrivez-vous {lexWorkshopAtPlural} qui vous intéressent.',
  'salon.fin.plan': 'Voir le plan',
  'salon.fin.listeOffres': 'Liste complète des offres',
  'salon.fin.listeExposants': '{lexExhibitorAllDefPluralCap}',
};
